/**
 * Promo product carousel
 * - Autoplaying slider of full product cards (media, variants, quantity, gift wrap, ATC)
 * - Each slide is self-contained: it never uses the Section Rendering API, so several
 *   products can live inside a single section without id collisions.
 */

const PROMO_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class PromoProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-promo-track]');
    this.viewport = this.querySelector('[data-promo-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-promo-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-promo-dot]'));
    this.prevButton = this.querySelector('[data-promo-prev]');
    this.nextButton = this.querySelector('[data-promo-next]');

    this.index = 0;
    this.paused = false;
    this.visible = true;
    this.timer = null;
    this.loop = this.dataset.loop === 'true';
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
  }

  connectedCallback() {
    if (!this.track || this.slides.length === 0) return;

    this.prevButton?.addEventListener('click', () => this.select(this.index - 1, true));
    this.nextButton?.addEventListener('click', () => this.select(this.index + 1, true));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.select(parseInt(dot.dataset.promoDot, 10), true));
    });

    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeyDown);

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.bindSwipe();
    this.observeVisibility();

    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
    }

    // Images arrive after first layout — re-measure so the arrows settle on the media.
    this.querySelectorAll('.promo-slide__media-main img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', () => this.positionArrows(), { once: true });
    });

    this.select(0);
    this.startAutoplay();

    // Theme editor: jump to the slide whose block was selected.
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  pause = () => {
    this.paused = true;
    this.stopAutoplay();
  };

  resume = () => {
    this.paused = false;
    this.startAutoplay();
  };

  onFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.resume();
  };

  onVisibilityChange = () => {
    if (document.hidden) {
      this.stopAutoplay();
    } else if (!this.paused && this.visible) {
      this.startAutoplay();
    }
  };

  onKeyDown = (event) => {
    if (event.target.closest('input, select, textarea, button')) return;
    if (event.key === 'ArrowLeft') this.select(this.index - 1, true);
    if (event.key === 'ArrowRight') this.select(this.index + 1, true);
  };

  onBlockSelect = (event) => {
    const slide = this.slides.findIndex((element) => element.contains(event.target) || element === event.target);
    if (slide === -1) return;
    this.pause();
    this.select(slide);
  };

  onBlockDeselect = (event) => {
    const slide = this.slides.findIndex((element) => element.contains(event.target) || element === event.target);
    if (slide === -1) return;
    this.resume();
  };

  observeVisibility() {
    if (!('IntersectionObserver' in window)) return;
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.visible = entry.isIntersecting;
          if (this.visible && !this.paused) {
            this.startAutoplay();
          } else {
            this.stopAutoplay();
          }
        });
      },
      { threshold: 0.25 }
    );
    this.intersectionObserver.observe(this);
  }

  bindSwipe() {
    let startX = null;
    let startY = null;

    this.viewport.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        this.pause();
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const deltaX = event.changedTouches[0].clientX - startX;
        const deltaY = event.changedTouches[0].clientY - startY;
        if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.select(deltaX < 0 ? this.index + 1 : this.index - 1, true);
        }
        startX = null;
        startY = null;
        this.resume();
      },
      { passive: true }
    );
  }

  select(index, userInitiated = false) {
    const count = this.slides.length;
    if (count === 0) return;

    let next = index;
    if (next < 0) next = this.loop ? count - 1 : 0;
    if (next > count - 1) next = this.loop ? 0 : count - 1;

    this.index = next;
    this.applyOffset();

    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === next);
      dot.setAttribute('aria-selected', i === next ? 'true' : 'false');
    });

    if (this.prevButton && !this.loop) this.prevButton.disabled = next === 0;
    if (this.nextButton && !this.loop) this.nextButton.disabled = next === count - 1;

    if (userInitiated) this.restartAutoplay();
  }

  /**
   * Offsets by the slide's real position instead of `index * 100%`, so an
   * over-wide slide can never leave a neighbouring slide peeking into view.
   */
  applyOffset() {
    const slide = this.slides[this.index];
    if (!slide) return;
    const offset = slide.offsetLeft - this.track.offsetLeft;
    this.track.style.transform = `translateX(-${offset}px)`;
    this.positionArrows();
  }

  /**
   * On mobile the slide stacks (media on top, info below), so a vertically
   * centred arrow lands on the price/variant area. Align the arrows to the
   * middle of the active slide's media instead.
   */
  positionArrows() {
    if (!this.prevButton && !this.nextButton) return;

    const stacked = window.matchMedia('(max-width: 749px)').matches;
    if (!stacked) {
      this.style.removeProperty('--promo-arrow-top');
      return;
    }

    const slide = this.slides[this.index];
    const media = slide?.querySelector('.promo-slide__media-main');
    if (!media) {
      this.style.removeProperty('--promo-arrow-top');
      return;
    }

    const mediaRect = media.getBoundingClientRect();
    if (mediaRect.height === 0) return;

    // Arrows are absolutely positioned against the carousel itself.
    const carouselRect = this.getBoundingClientRect();
    const center = mediaRect.top - carouselRect.top + mediaRect.height / 2;
    this.style.setProperty('--promo-arrow-top', `${Math.round(center)}px`);
  }

  onResize = () => {
    // Skip the transition while re-aligning after a resize / orientation change.
    const transition = this.track.style.transition;
    this.track.style.transition = 'none';
    this.applyOffset();
    requestAnimationFrame(() => {
      this.track.style.transition = transition;
    });
  };

  startAutoplay() {
    if (!this.autoplayEnabled || PROMO_REDUCED_MOTION.matches) return;
    if (this.paused || document.hidden || !this.visible || this.timer) return;
    this.timer = setInterval(() => this.select(this.index + 1), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

class PromoProductSlide extends HTMLElement {
  constructor() {
    super();
    this.uid = this.dataset.uid;
    this.productTitle = this.dataset.productTitle;
    this.form = this.querySelector('form');
    this.variantIdInput = this.querySelector('[data-promo-variant-id]');
    this.submitButton = this.querySelector('[data-promo-submit]');
    this.submitButtonText = this.querySelector('[data-promo-submit-text]');
    this.errorElement = this.querySelector('[data-promo-error]');
    this.optionInputs = Array.from(this.querySelectorAll('[data-promo-option]'));

    this.variants = this.parseJSON('[data-promo-variant-data]') || [];
    this.strings = this.parseJSON('[data-promo-strings]') || {};
    this.currentVariant = this.variants.find(
      (variant) => String(variant.id) === String(this.variantIdInput?.value)
    );
  }

  connectedCallback() {
    this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.bindMedia();
    this.bindGiftWrap();
  }

  parseJSON(selector) {
    const element = this.querySelector(selector);
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('[promo-product-slide] invalid JSON', selector, error);
      return null;
    }
  }

  /* ---------------- Variants ---------------- */

  selectedOptionValues() {
    const values = [];
    this.optionInputs.forEach((input) => {
      const position = parseInt(input.dataset.optionPosition, 10) - 1;
      if (input.tagName === 'SELECT') {
        values[position] = input.value;
      } else if (input.checked) {
        values[position] = input.value;
      }
    });
    return values;
  }

  onOptionChange() {
    const selected = this.selectedOptionValues();

    this.querySelectorAll('[data-promo-option-selected]').forEach((element) => {
      const position = parseInt(element.dataset.promoOptionSelected, 10) - 1;
      if (selected[position]) element.textContent = selected[position];
    });

    const variant = this.variants.find((candidate) =>
      candidate.options.every((value, index) => value === selected[index])
    );

    this.currentVariant = variant;
    this.updatePrice(variant);
    this.updateSku(variant);
    this.updateButton(variant);

    if (variant) {
      this.variantIdInput.value = variant.id;
      this.variantIdInput.disabled = !variant.available;
      if (variant.media_id) this.showMedia(variant.media_id);
    }
  }

  updatePrice(variant) {
    const wrapper = this.querySelector('[data-promo-price]');
    if (!wrapper) return;

    const regular = wrapper.querySelector('[data-promo-price-regular]');
    const compare = wrapper.querySelector('[data-promo-price-compare]');
    const unavailable = wrapper.querySelector('[data-promo-unavailable]');

    if (!variant) {
      regular?.classList.add('hidden');
      compare?.classList.add('hidden');
      unavailable?.classList.remove('hidden');
      return;
    }

    unavailable?.classList.add('hidden');
    if (regular) {
      regular.classList.remove('hidden');
      regular.textContent = variant.price;
      regular.classList.toggle('promo-slide__price--on-sale', Boolean(variant.compare_at_price));
    }
    if (compare) {
      compare.textContent = variant.compare_at_price || '';
      compare.classList.toggle('hidden', !variant.compare_at_price);
    }
  }

  updateSku(variant) {
    const sku = this.querySelector('[data-promo-sku]');
    if (sku) sku.textContent = variant?.sku || '';
  }

  updateButton(variant) {
    if (!this.submitButton) return;

    if (!variant) {
      this.submitButton.setAttribute('aria-disabled', 'true');
      if (this.submitButtonText) this.submitButtonText.textContent = this.strings.unavailable || 'Unavailable';
      return;
    }

    if (variant.available) {
      this.submitButton.removeAttribute('aria-disabled');
      if (this.submitButtonText) this.submitButtonText.textContent = this.strings.addToCart || 'Add to cart';
    } else {
      this.submitButton.setAttribute('aria-disabled', 'true');
      if (this.submitButtonText) this.submitButtonText.textContent = this.strings.soldOut || 'Sold out';
    }
  }

  /* ---------------- Media ---------------- */

  bindMedia() {
    this.mediaItems = Array.from(this.querySelectorAll('[data-promo-media-item]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-promo-thumb]'));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
    });
  }

  showMedia(mediaId) {
    if (!this.mediaItems || this.mediaItems.length === 0) return;
    const target = String(mediaId);
    let matched = false;

    this.mediaItems.forEach((item) => {
      const active = item.dataset.mediaId === target;
      if (active) matched = true;
      item.classList.toggle('is-active', active);
    });

    if (!matched) return;

    this.thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === target);
    });
  }

  /* ---------------- Gift wrap ---------------- */

  bindGiftWrap() {
    this.giftWrap = this.querySelector('[data-promo-gift-wrap]');
    if (!this.giftWrap) return;

    const toggle = this.giftWrap.querySelector('[data-promo-gift-toggle]');
    const content = this.giftWrap.querySelector('[data-promo-gift-content]');
    const current = this.giftWrap.querySelector('[data-promo-gift-current]');

    toggle?.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      content?.classList.toggle('is-expanded', !expanded);
    });

    this.giftWrap.querySelectorAll('[data-promo-gift-input]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!current) return;
        if (input.value === 'none') {
          current.textContent = this.strings.giftNone || 'No';
          return;
        }
        const label = this.giftWrap.querySelector(`label[for="${input.id}"]`);
        const name = label?.querySelector('.option-name')?.textContent.trim() || '';
        const price = label?.querySelector('.option-price')?.textContent.trim() || '';
        current.textContent = [name, price].filter(Boolean).join(' ');
      });
    });

    this.giftWrap.querySelectorAll('[data-promo-gift-image]').forEach((image) => {
      image.addEventListener('click', (event) => {
        event.preventDefault();
        this.openGiftWrapModal(image.dataset.fullSrc, image.dataset.title);
      });
    });
  }

  openGiftWrapModal(src, title) {
    if (!src) return;

    const modal = document.createElement('div');
    modal.className = 'promo-gift-modal';
    modal.innerHTML = `
      <div class="promo-gift-modal__content" role="dialog" aria-modal="true" aria-label="${title || ''}">
        <button type="button" class="promo-gift-modal__close" aria-label="Close">&times;</button>
        <img class="promo-gift-modal__image" src="${src}" alt="${title || ''}">
        <p class="promo-gift-modal__title">${title || ''}</p>
      </div>
    `;

    const close = () => {
      modal.remove();
      document.removeEventListener('keydown', onKeyDown);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };

    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target.closest('.promo-gift-modal__close')) close();
    });
    document.addEventListener('keydown', onKeyDown);

    document.body.appendChild(modal);
    requestAnimationFrame(() => modal.classList.add('is-active'));
  }

  selectedGiftWrapVariant() {
    if (!this.giftWrap) return null;
    const selected = this.giftWrap.querySelector('[data-promo-gift-input]:checked');
    if (!selected || selected.value === 'none') return null;
    return selected.dataset.variantId || null;
  }

  /* ---------------- Add to cart ---------------- */

  get cart() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  quantity() {
    const input = this.querySelector('input[name="quantity"]');
    const value = input ? parseInt(input.value, 10) : 1;
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  onSubmit(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.showError();
    this.setLoading(true);

    const quantity = this.quantity();
    const items = [{ id: Number(this.variantIdInput.value), quantity }];

    const giftVariantId = this.selectedGiftWrapVariant();
    if (giftVariantId) {
      const propertyName = this.strings.giftProperty || 'Gift wrap for';
      items.push({
        id: Number(giftVariantId),
        quantity,
        properties: { [propertyName]: this.productTitle },
      });
    }

    const body = { items };
    const cart = this.cart;
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement(document.activeElement);
    }

    fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.showError(response.description || response.message);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'promo-product-slide',
              productVariantId: this.variantIdInput.value,
              errors: response.errors || response.description,
              message: response.message,
            });
          }
          return;
        }

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const addedItems = response.items || [];
        cart.renderContents({
          ...response,
          key: addedItems[0]?.key,
          id: addedItems[0]?.id,
        });

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'promo-product-slide',
            productVariantId: this.variantIdInput.value,
            cartData: response,
          });
        }
      })
      .catch((error) => {
        console.error('[promo-product-slide] add to cart failed', error);
        this.showError();
      })
      .finally(() => {
        this.setLoading(false);
        if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      });
  }

  setLoading(loading) {
    const spinner = this.submitButton?.querySelector('.loading__spinner');
    this.submitButton?.classList.toggle('loading', loading);
    spinner?.classList.toggle('hidden', !loading);
    if (loading) {
      this.submitButton?.setAttribute('aria-disabled', 'true');
    } else if (this.currentVariant?.available !== false) {
      this.submitButton?.removeAttribute('aria-disabled');
    }
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message || '';
    this.errorElement.toggleAttribute('hidden', !message);
  }
}

if (!customElements.get('promo-product-slide')) {
  customElements.define('promo-product-slide', PromoProductSlide);
}

if (!customElements.get('promo-product-carousel')) {
  customElements.define('promo-product-carousel', PromoProductCarousel);
}
